import React from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { Box, Chip, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import * as R from 'ramda';

// Components
import ListTabSkeleton from './ListTabSkeleton';

// styles
import * as styles from './ListTabs.styles';

// Props of importance:
// the singleChip bool of counts vs the double counts on the tabs
//
// labelCounts must be an object that has a keys named similar to id
// values are either single string, or active/inactive
// ex: { baseProduct: '1 of 100', ... }
//
// tabs must be an array of objects
// ex: [{ id: 'baseProducts', name: 'Base Products', shortName: 'Base', icon: 'room_rounded' }, ...]
// the icon key/value is optional
const ListTabs = ({ activeTab, singleChip, labelCounts, onChange, pendingState, tabs }) => {
  const theme = useTheme();
  const lgUp = useMediaQuery(t => t.breakpoints.up('lg'), { noSsr: true });

  const tabsContainerSx = [
    styles.tabsContainer(theme),
    !lgUp && styles.tabsContainerMobile(theme),
    tabs.length === 1 && styles.tabsSingle,
  ];

  const dualCountSx = [styles.boxStyles(theme, { singleChip }), styles.border(theme)];

  // This renders the counts for the singleChip
  const renderCount = (type, productType) => {
    const counters = labelCounts[productType] ?? [];
    return R.countBy(R.prop('active'))(counters)[type];
  };

  const renderChip = (id, name) =>
    singleChip ? (
      <Chip
        label={labelCounts[name] || 0}
        sx={{
          ...styles.iconRoot(theme),
          '& .MuiChip-label': styles.chipLabel(theme),
        }}
      />
    ) : (
      <Box sx={styles.dualCount}>
        <Chip
          label={renderCount(true, id) ?? 0}
          sx={{
            ...styles.boxStyles(theme, { singleChip }),
            backgroundColor: 'primary.lighter',
          }}
        />
        <Chip
          label={renderCount(false, id) ?? 0}
          sx={[...dualCountSx, { backgroundColor: 'surface.white' }]}
        />
      </Box>
    );

  const isActiveTab = id => R.and(R.equals(id, activeTab), singleChip);

  const Label = ({ id, name, shortName }) => (
    <Box sx={{ display: 'flex' }}>
      <Typography
        component="span"
        sx={{
          color: isActiveTab(id) ? 'primary.dark' : 'text.link',
          height: '1.82ch',
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          mr: { xs: 0, lg: 1 },
          textTransform: 'none',
          whiteSpace: 'nowrap',
        }}
        variant="button"
      >
        {lgUp ? name : shortName}
      </Typography>
      {lgUp && renderChip(id, shortName)}
    </Box>
  );

  Label.propTypes = {
    id: string.isRequired,
    name: string.isRequired,
    shortName: string.isRequired,
  };

  const renderTabs = () => (
    <Tabs onChange={R.flip(onChange)} sx={tabsContainerSx} value={activeTab}>
      {tabs.map(({ id, ...rest }) => (
        <Tab key={id} label={<Label id={id} {...rest} />} value={id} />
      ))}
    </Tabs>
  );

  const tabsOrSkeletons = R.ifElse(
    R.both(R.identity, R.equals(lgUp)),
    R.always(<ListTabSkeleton />),
    renderTabs
  );

  return tabsOrSkeletons(pendingState);
};

ListTabs.propTypes = {
  activeTab: string,
  labelCounts: shape({}).isRequired,
  onChange: func.isRequired,
  pendingState: bool.isRequired,
  singleChip: bool,
  tabs: arrayOf(shape({})).isRequired,
};

ListTabs.defaultProps = {
  activeTab: '',
  singleChip: false,
};

export default ListTabs;
